/**
 * The address of the CRM card tab (#745): `?entityType=&entityId=` as the entry
 * point `/api/v1/embedded/app` puts them there from Bitrix24's placement. The
 * address is the only thing that names the card, so anything that does not
 * read as one of the four card types and a positive id of digits goes to the
 * dashboard — never a tab with a half-understood entity.
 */
import { redirect } from '@tanstack/react-router';
import {
  parseCrmTabEntityId,
  crmTabEntityTypeSchema,
  type CrmTabEntityType,
} from '@aica/api-types';

export interface CrmTabSearch {
  entityType?: CrmTabEntityType;
  entityId?: string;
  /** Bitrix24's placement code, kept so a navigation does not drop it. */
  placement?: string;
}

/** A placement code is a short upper-case token (`CRM_DEAL_DETAIL_TAB`). */
const PLACEMENT_PATTERN = /^[A-Z0-9_]{1,64}$/;

export function parseCrmTabSearch(search: Record<string, unknown>): CrmTabSearch {
  const entityType = crmTabEntityTypeSchema.safeParse(search.entityType);
  // TanStack parses `?entityId=123` as the NUMBER 123: the same reading as the
  // entry point's (`parseCrmTabEntityId`), so a number past the safe range goes
  // to the dashboard. Bitrix24's ids are far below 2^53; the router's app-wide
  // parsing is deliberately left as it is for this edge.
  const entityId = parseCrmTabEntityId(search.entityId);
  const placement =
    typeof search.placement === 'string' && PLACEMENT_PATTERN.test(search.placement)
      ? search.placement
      : undefined;
  return {
    entityType: entityType.success ? entityType.data : undefined,
    entityId: entityId ?? undefined,
    placement,
  };
}

/** The route's `beforeLoad`: no readable card in the address — the dashboard. */
export function requireCrmTabEntity({ search }: { search: CrmTabSearch }): void {
  if (!search.entityType || !search.entityId) {
    throw redirect({ to: '/embedded/dashboard', replace: true });
  }
}
